import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { HtmlToPdfResult, renderHtmlToPdfWithFields } from "../../../services/signaturesHtmlToPdf";
import { base64ToBytes } from "../../../utils/base64Bytes";

const loadViewer = () => import("../../sign/SignaturePdfPages");
const SignaturePdfPages = lazy(() => loadViewer().then((module) => ({ default: module.SignaturePdfPages })));
/** Typing pauses this long before a NEW revision is compiled; the first is not delayed. */
const RECOMPILE_DEBOUNCE_MS = 500;

interface SignatureDocumentPreviewProps {
  html?: string;
  url?: string;
  zoom?: number;
  prefilledValues?: Record<string, string>;
}

interface PreviewDocument {
  id: string;
  html?: string;
  url?: string;
  attempt: number;
  source: string | Uint8Array;
  artifact?: HtmlToPdfResult;
}

/** Keep the last painted PDF visible until the current input's replacement is painted. */
export function SignatureDocumentPreview({ html, url, zoom = 1, prefilledValues }: SignatureDocumentPreviewProps) {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement | null>(null);
  const sequence = useRef(0);
  const [width, setWidth] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [prepared, setPrepared] = useState<PreviewDocument | null>(null);
  const [displayed, setDisplayed] = useState<PreviewDocument | null>(null);
  const [error, setError] = useState<{ html?: string; url?: string; attempt: number } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [pendingHeight, setPendingHeight] = useState<{ id: string; height: number } | null>(null);
  const restoreScroll = useRef<{ top: number; left: number } | null>(null);
  const latest = useRef({ html, url, attempt });
  latest.current = { html, url, attempt };
  // The first compilation starts at once; only later edits wait out the debounce.
  const requestedCompile = useRef(false);
  const direct = useMemo<PreviewDocument | null>(() => url
    ? { id: "url:" + url + ":" + attempt, url, attempt, source: url }
    : null, [url, attempt]);
  const isCurrent = (document: PreviewDocument) => document.attempt === attempt &&
    (url ? document.url === url : document.html === html && !document.url);
  const hasError = !!error && error.html === html && error.url === url && error.attempt === attempt;
  const desired = direct || (prepared && prepared.html === html && prepared.attempt === attempt ? prepared : null);
  const candidate = !hasError && desired && (!displayed || !isCurrent(displayed)) ? desired : null;
  const updating = !!displayed && !isCurrent(displayed);
  const layers = [displayed, candidate].filter((document): document is PreviewDocument => !!document);

  useEffect(() => {
    const element = container.current;
    if (!element) return undefined;
    const measure = () => setWidth(element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The PDF viewer's code and its pdf.js worker load while the server compiles,
  // instead of after the compilation arrives.
  useEffect(() => {
    loadViewer().then((module) => module.preloadSignaturePdfWorker?.()).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (url || !html || (displayed?.html === html && displayed.attempt === attempt)) return undefined;
    const timer = window.setTimeout(() => {
      requestedCompile.current = true;
      renderHtmlToPdfWithFields(html).then((result) => {
        if (cancelled || latest.current.html !== html || latest.current.url || latest.current.attempt !== attempt) return;
        setPrepared({ id: "prepared:" + ++sequence.current, html, attempt, artifact: result,
          source: base64ToBytes(result.pdfBase64) });
      }).catch(() => {
        if (!cancelled && latest.current.html === html && !latest.current.url && latest.current.attempt === attempt) setError({ html, url, attempt });
      });
    }, requestedCompile.current ? RECOMPILE_DEBOUNCE_MS : 0);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [html, url, attempt, displayed?.html, displayed?.attempt]);

  useLayoutEffect(() => {
    const position = restoreScroll.current;
    if (position && container.current) {
      container.current.scrollTop = position.top;
      container.current.scrollLeft = position.left;
      setScrollTop(container.current.scrollTop);
      restoreScroll.current = null;
    }
  }, [displayed]);

  const pageWidth = Math.min(794, width) * zoom;
  // A shorter replacement must paint its eventual, clamped viewport even
  // when the reader is currently below its new final page.
  const stagedTop = candidate && pendingHeight?.id === candidate.id
    ? Math.max(0, scrollTop - Math.max(0, pendingHeight.height - (container.current?.clientHeight || 0)))
    : 0;
  const loading = <Box role="status" sx={{ p: 4, textAlign: "center" }}>
    <CircularProgress size={28} />
    <Typography sx={{ mt: 1 }}>{t("signatures.pdfViewer.loading")}</Typography>
  </Box>;
  const problem = <Box role="alert" sx={{ p: 2, bgcolor: "background.paper", textAlign: "center" }}>
    <Typography>{t(displayed ? "signatures.pdfViewer.updateError" : "signatures.pdfViewer.loadError")}</Typography>
    <Button onClick={() => setAttempt((value) => value + 1)}>{t("signatures.pdfViewer.retry")}</Button>
  </Box>;

  return (
    <Box sx={{ position: "relative", display: "flex", flex: 1, minWidth: 0, minHeight: 0 }}>
    <Box ref={container} data-signature-preview-scroll="" onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      aria-busy={updating && !hasError}
      sx={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "scroll", WebkitOverflowScrolling: "touch", direction: "ltr" }}>
      {displayed && hasError && <Box sx={{ position: "sticky", top: 0, height: 0, zIndex: 2 }}>{problem}</Box>}
      <Box sx={{ position: "relative", width: pageWidth, mx: "auto" }}>
        {pageWidth > 0 && layers.map((document) => {
          const staged = !!displayed && document !== displayed;
          const artifact = document.artifact;
          return <Box key={document.id} data-signature-preview-staged={staged ? "true" : undefined}
            data-signature-preview-hash={!staged ? artifact?.pdfSha256 : undefined}
            data-signature-preview-current={!staged ? String(isCurrent(document)) : undefined}
            aria-hidden={staged || undefined}
            style={staged ? { position: "absolute", inset: 0, overflow: "hidden", opacity: 0, visibility: "hidden", pointerEvents: "none" } : undefined}>
            <Box style={staged ? { position: "relative", top: stagedTop } : undefined}>
              <Suspense fallback={loading}>
                <SignaturePdfPages source={document.source} width={pageWidth} expectedPageCount={artifact?.totalPages}
                  onLoad={(pages) => {
                    if (document === displayed) return;
                    const height = pages.reduce((sum, page) => sum + page.height, 0);
                    setPendingHeight((previous) => previous?.id === document.id && previous.height === height ? previous : { id: document.id, height });
                  }}
                  onError={() => {
                    if (latest.current.attempt === document.attempt && (latest.current.url
                      ? latest.current.url === document.url
                      : latest.current.html === document.html && !document.url)) {
                      setError({ ...latest.current });
                    }
                  }}
                  onReadyChange={(ready) => {
                    if (!ready || document === displayed || latest.current.attempt !== document.attempt) return;
                    if (latest.current.url ? document.url !== latest.current.url : document.html !== latest.current.html || document.url) return;
                    restoreScroll.current = { top: container.current?.scrollTop || 0, left: container.current?.scrollLeft || 0 };
                    setDisplayed(document);
                  }}
                  renderOverlay={artifact ? (page) => artifact.fields.flatMap((field, index) =>
                    (field.areas || []).filter((area) => area.page === page.page).map((area, areaIndex) => (
                      <Box key={index + "-" + areaIndex}
                        style={{ position: "absolute", left: area.x * page.width, top: area.y * page.height,
                          width: area.w * page.width, height: area.h * page.height }}
                        sx={{ boxSizing: "border-box", border: "1px dashed", borderColor: "primary.main",
                          bgcolor: "rgba(205,220,57,0.16)", display: "flex", alignItems: "center",
                          justifyContent: "center", overflow: "hidden", direction: "rtl",
                          fontSize: Math.max(7, (field.fontSize || 12) * page.width / 794), lineHeight: 1.15,
                          color: "text.primary", pointerEvents: "none" }}>{
                            !field.readonly && ["text", "phone", "number", "date", "cells", "select", "radio"].includes(field.type)
                              ? prefilledValues?.[field.name]?.trim() || field.title || field.name
                              : field.title || field.name
                          }</Box>
                    ))
                  ) : undefined} />
              </Suspense>
            </Box>
          </Box>;
        })}
        {!layers.length && (hasError ? problem : loading)}
      </Box>
    </Box>
    {updating && !hasError && <Box role="status" aria-live="polite" data-signature-preview-loading=""
      sx={{ position: "absolute", inset: 0, zIndex: 3, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.65), cursor: "progress" }}>
      <Box sx={{ p: 3, mx: 2, borderRadius: 2, bgcolor: "background.paper", boxShadow: 3, textAlign: "center" }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 1.5 }} fontWeight={600}>{t("signatures.pdfViewer.updating")}</Typography>
      </Box>
    </Box>}
    </Box>
  );
}
